import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPut, apiDelete } from '../utils/api';
import styles from './FriendRequestsPage.module.css';

const FriendRequestsPage = () => {
  const navigate = useNavigate();
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchFriendRequests();
  }, []);

  const fetchFriendRequests = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch both received and sent requests
      const [receivedResponse, sentResponse] = await Promise.all([
        apiGet('/friendships/requests'),
        apiGet('/friendships/sent')
      ]);

      if (receivedResponse.ok) {
        const receivedData = await receivedResponse.json();
        setReceivedRequests(receivedData);
      }

      if (sentResponse.ok) {
        const sentData = await sentResponse.json();
        setSentRequests(sentData);
      }
    } catch (error) {
      console.error('Error fetching friend requests:', error);
      setError('Failed to load friend requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (friendshipId) => {
    try {
      setActionLoading(prev => ({ ...prev, [friendshipId]: 'accepting' }));
      
      const response = await apiPut(`/friendships/${friendshipId}/accept`);
      
      if (response.ok) {
        // Remove from received requests
        setReceivedRequests(prev => prev.filter(req => req.id !== friendshipId));
      } else {
        setError('Failed to accept friend request');
      }
    } catch (error) {
      console.error('Error accepting friend request:', error);
      setError('Failed to accept friend request');
    } finally {
      setActionLoading(prev => ({ ...prev, [friendshipId]: false }));
    }
  };

  const handleDeclineRequest = async (friendshipId) => {
    try {
      setActionLoading(prev => ({ ...prev, [friendshipId]: 'declining' }));
      
      const response = await apiPut(`/friendships/${friendshipId}/decline`);
      
      if (response.ok) {
        // Remove from received requests
        setReceivedRequests(prev => prev.filter(req => req.id !== friendshipId));
      } else {
        setError('Failed to decline friend request');
      }
    } catch (error) {
      console.error('Error declining friend request:', error);
      setError('Failed to decline friend request');
    } finally {
      setActionLoading(prev => ({ ...prev, [friendshipId]: false }));
    }
  };

  const handleCancelSentRequest = async (friendshipId) => {
    try {
      setActionLoading(prev => ({ ...prev, [friendshipId]: 'cancelling' }));
      
      const response = await apiDelete(`/friendships/${friendshipId}`);
      
      if (response.ok) {
        // Remove from sent requests
        setSentRequests(prev => prev.filter(req => req.id !== friendshipId));
      } else {
        setError('Failed to cancel friend request');
      }
    } catch (error) {
      console.error('Error cancelling friend request:', error);
      setError('Failed to cancel friend request');
    } finally {
      setActionLoading(prev => ({ ...prev, [friendshipId]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={styles.friendRequestsPage}>
        <div className={styles.loading}>Loading friend requests...</div>
      </div>
    );
  }

  return (
    <div className={styles.friendRequestsPage}>
      <div className={styles.pageHeader}>
        <h1>Friend Requests</h1>
        <button 
          className={`btn btn-secondary ${styles.backButton}`}
          onClick={() => navigate('/friends')}
        >
          Back to Friends
        </button>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      <div className={styles.requestsContainer}>
        {/* Received Requests */}
        <div className={styles.requestsSection}>
          <h2>Friend Requests Received</h2>
          {receivedRequests.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No pending friend requests</p>
            </div>
          ) : (
            <div className={styles.requestsList}>
              {receivedRequests.map(request => (
                <div key={request.id} className={styles.requestCard}>
                  <div className={styles.userInfo}>
                    <div className={styles.avatar}>
                      {request.requester.first_name.charAt(0)}{request.requester.last_name.charAt(0)}
                    </div>
                    <div className={styles.userDetails}>
                      <h3>{request.requester.first_name} {request.requester.last_name}</h3>
                      <p>{request.requester.email}</p>
                      <span className={styles.requestDate}>
                        Sent {formatDate(request.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.requestActions}>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleAcceptRequest(request.id)}
                      disabled={actionLoading[request.id]}
                    >
                      {actionLoading[request.id] === 'accepting' ? 'Accepting...' : 'Accept'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleDeclineRequest(request.id)}
                      disabled={actionLoading[request.id]}
                    >
                      {actionLoading[request.id] === 'declining' ? 'Declining...' : 'Decline'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sent Requests */}
        <div className={styles.requestsSection}>
          <h2>Friend Requests Sent</h2>
          {sentRequests.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No pending sent requests</p>
            </div>
          ) : (
            <div className={styles.requestsList}>
              {sentRequests.map(request => (
                <div key={request.id} className={styles.requestCard}>
                  <div className={styles.userInfo}>
                    <div className={styles.avatar}>
                      {request.receiver.first_name.charAt(0)}{request.receiver.last_name.charAt(0)}
                    </div>
                    <div className={styles.userDetails}>
                      <h3>{request.receiver.first_name} {request.receiver.last_name}</h3>
                      <p>{request.receiver.email}</p>
                      <span className={styles.requestDate}>
                        Sent {formatDate(request.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.requestActions}>
                    <span className={styles.pendingStatus}>Pending</span>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleCancelSentRequest(request.id)}
                      disabled={actionLoading[request.id]}
                    >
                      {actionLoading[request.id] === 'cancelling' ? 'Cancelling...' : 'Cancel'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FriendRequestsPage;
